import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { DollarSign, TrendingUp, TrendingDown, Calendar, Users } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface RevenueData {
  month: string;
  revenue: number;
  subscriptions: number;
  growth: number;
}

const AdminRevenue = () => {
  const [timeRange, setTimeRange] = useState('12');

  const { data: revenueStats, isLoading: statsLoading } = useQuery({
    queryKey: ['admin-revenue-stats'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_revenue_stats');
      if (error) throw error;
      return data?.[0] || { total_revenue: 0, monthly_revenue: 0, avg_subscription_value: 0 };
    }
  });

  const { data: monthlyRevenue, isLoading: monthlyLoading } = useQuery({
    queryKey: ['admin-monthly-revenue', timeRange],
    queryFn: async () => {
      // Simulated data - in production, this would come from analytics table
      const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
      return months.map((month, index) => ({
        month,
        revenue: Math.floor(Math.random() * 5000) + 1000,
        subscriptions: Math.floor(Math.random() * 50) + 10,
        growth: Math.floor(Math.random() * 40) - 20
      }));
    }
  });

  const { data: topTrainers, isLoading: trainersLoading } = useQuery({
    queryKey: ['admin-top-trainers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, subscription_plan, subscription_status')
        .eq('is_trainer', true)
        .eq('subscription_status', 'active')
        .limit(10);
      
      if (error) throw error;
      return data || [];
    }
  });

  const chartConfig = {
    revenue: {
      label: "Receita",
      color: "#2563eb",
    },
    subscriptions: {
      label: "Subscrições",
      color: "#dc2626",
    },
  };

  if (statsLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const totalRevenue = revenueStats?.total_revenue ?? 0;
  const monthlyRevenueValue = revenueStats?.monthly_revenue ?? 0;
  const avgSubscriptionValue = revenueStats?.avg_subscription_value ?? 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Rentabilidade</h1>
          <p className="text-gray-600">Análise de receitas e performance financeira</p>
        </div>
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Período" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="6">Últimos 6 meses</SelectItem>
            <SelectItem value="12">Últimos 12 meses</SelectItem>
            <SelectItem value="24">Últimos 24 meses</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Receita Total</CardTitle>
            <DollarSign className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">€{Number(totalRevenue).toFixed(2)}</div>
            <p className="text-xs text-gray-600">Todas as subscrições</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Receita Mensal Atual</CardTitle>
            <TrendingUp className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">€{Number(monthlyRevenueValue).toFixed(2)}</div>
            <p className="text-xs text-gray-600">Subscrições ativas</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Valor Médio por Subscrição</CardTitle>
            <Users className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">€{Number(avgSubscriptionValue).toFixed(2)}</div>
            <p className="text-xs text-gray-600">Por utilizador ativo</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <Tabs defaultValue="revenue" className="space-y-4">
        <TabsList>
          <TabsTrigger value="revenue">Receitas</TabsTrigger>
          <TabsTrigger value="growth">Crescimento</TabsTrigger>
          <TabsTrigger value="trainers">Top Trainers</TabsTrigger>
        </TabsList>

        <TabsContent value="revenue">
          <Card>
            <CardHeader>
              <CardTitle>Evolução da Receita</CardTitle>
              <CardDescription>
                Receita mensal e número de subscrições nos últimos {timeRange} meses
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer config={chartConfig} className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthlyRevenue}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Line type="monotone" dataKey="revenue" stroke="#2563eb" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="growth">
          <Card>
            <CardHeader>
              <CardTitle>Taxa de Crescimento</CardTitle>
              <CardDescription>
                Crescimento mensal em percentagem
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer config={chartConfig} className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyRevenue}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="growth" fill="#dc2626" />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trainers">
          <Card>
            <CardHeader>
              <CardTitle>Personal Trainers Ativos</CardTitle>
              <CardDescription>
                Trainers com subscrições ativas que geram receita
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Plano</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topTrainers?.map((trainer) => (
                    <TableRow key={trainer.id}>
                      <TableCell className="font-medium">
                        {trainer.first_name} {trainer.last_name}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {trainer.subscription_plan || 'Free'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="default">
                          {trainer.subscription_status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminRevenue;
